import { datetimeLocalToUtcIso } from './timezone';
import { assessMedicationCalendar, type MedicationCalendarInput } from './medication-calendar';

/**
 * Canonical medication frequency → daily time-slot convention (24h "HH:MM",
 * tenant-local). Single source of truth shared by the web MAR grid and the
 * mobile eMAR round worklist so the two never disagree on when a dose is due.
 *
 * NOTE: this is a frequency-based convention, NOT a per-resident dose schedule
 * (the platform has no discrete scheduled-dose model — `medications` carries
 * `frequency` + `schedule` text only). Both the web MAR and the mobile feed
 * present these convention times; a resident with a genuinely different dose
 * time is a known limitation of the shared model, not a mobile-only gap.
 * `medications.schedule` is controlled-drug classification, not recurrence
 * timing. Calendar anchors supplied to expansion must be confirmed prescription
 * facts; neither that classification nor a first-view date supplies an anchor.
 */
export const MEDICATION_FREQUENCY_TIMES: Record<string, string[]> = {
  once_daily: ['08:00'],
  twice_daily: ['08:00', '20:00'],
  three_times_daily: ['08:00', '14:00', '20:00'],
  four_times_daily: ['06:00', '12:00', '18:00', '22:00'],
  weekly: ['08:00'],
  fortnightly: ['08:00'],
  monthly: ['08:00'],
  as_needed: [],
  // Clinical shorthand slots: mane = morning dose, nocte = night dose.
  mane: ['08:00'],
  nocte: ['20:00'],
};

/** Administration statuses that count a scheduled dose as completed (given). */
export const DOSE_GIVEN_STATUSES = new Set(['ADMINISTERED', 'SELF_ADMINISTERED']);
/**
 * Statuses that close a dose without it being given (actioned, not pending).
 * MISSED is a terminal recorded exception (not a placeholder), so it belongs
 * here — otherwise a dose closed as MISSED falls through to time-bucketing and
 * wrongly resurfaces as overdue/outstanding, prompting re-administration.
 */
export const DOSE_NOT_GIVEN_STATUSES = new Set(['REFUSED', 'WITHHELD', 'NOT_AVAILABLE', 'MISSED']);

/**
 * Clinical precedence when multiple administrations land on one scheduled cell:
 * a given dose outranks a not-given action, which outranks the SCHEDULED
 * placeholder, which outranks an unknown status. Mirrors the web MAR's
 * pickPreferredAdministration for the shared statuses (given 4 / not-given 3 /
 * scheduled 2) so the two surfaces resolve a collision the same way and a given
 * dose can never be displayed as not-given.
 */
export function medicationAdministrationStatusPriority(status: string | null | undefined): number {
  if (!status) return 0;
  if (DOSE_GIVEN_STATUSES.has(status)) return 4;
  if (DOSE_NOT_GIVEN_STATUSES.has(status)) return 3;
  if (status === 'SCHEDULED') return 2;
  return 1;
}

export type DoseStatusBucket = 'given' | 'not_given' | 'overdue' | 'due' | 'upcoming';

/** The convention time slots for a frequency (empty for PRN/as_needed/unknown). */
/** Clinical shorthand → canonical scheduler keys (case-insensitive). */
const FREQUENCY_SHORTHAND: Record<string, string> = {
  DAILY: 'once_daily',
  BD: 'twice_daily',
  TDS: 'three_times_daily',
  QID: 'four_times_daily',
  WEEKLY: 'weekly',
  FORTNIGHTLY: 'fortnightly',
  MONTHLY: 'monthly',
  PRN: 'as_needed',
  MANE: 'mane',
  NOCTE: 'nocte',
  // STAT is a one-off immediate dose — intentionally no recurring slots.
  STAT: 'as_needed',
};

export function scheduledTimesForFrequency(frequency: string | null | undefined): string[] {
  if (!frequency) return [];
  const raw = String(frequency).trim();
  // Shorthand normalisation lives HERE so web MAR, mobile eMAR, and the
  // client chart schedule identically (adversarial review round-4).
  const canonical = MEDICATION_FREQUENCY_TIMES[raw]
    ? raw
    : (FREQUENCY_SHORTHAND[raw.toUpperCase()] ?? raw.toLowerCase());
  return MEDICATION_FREQUENCY_TIMES[canonical] ?? [];
}

export interface MedicationDoseSlot {
  /** "HH:MM" tenant-local slot label. */
  timeSlot: string;
  /** UTC ISO instant of the slot for the given date in the tenant timezone. */
  instantIso: string;
}

/**
 * Expand a medication into its scheduled dose slots for a given local date,
 * resolving each slot to a UTC instant in the tenant timezone. PRN / as_needed
 * (and unknown/unconfirmed calendar frequencies) yield no slots. Non-daily
 * recurrence requires a confirmed anchor and applicable monthly policy;
 * prescription dates are evaluated before convention times. No slots does not
 * authorise withholding care or erase administration history.
 */
export function expandMedicationDoseSlots(
  medication: MedicationCalendarInput & { isPrn?: boolean | null },
  localDate: string,
  tz: string,
): MedicationDoseSlot[] {
  if (medication.isPrn) return [];
  if (assessMedicationCalendar(medication, localDate).state !== 'eligible') return [];
  const slots: MedicationDoseSlot[] = [];
  for (const timeSlot of scheduledTimesForFrequency(medication.frequency)) {
    const instantIso = datetimeLocalToUtcIso(`${localDate}T${timeSlot}`, tz);
    if (instantIso) slots.push({ timeSlot, instantIso });
  }
  return slots;
}

/**
 * Bucket a scheduled dose for the eMAR worklist. A recorded administration wins
 * (given / not_given); otherwise the dose is placed by time relative to `nowMs`
 * using an administration window of ±`dueWindowMinutes` (default 60) around the
 * slot instant:
 *   • more than the window in the future → upcoming
 *   • within the window (either side)     → due
 *   • more than the window in the past    → overdue
 */
export function bucketDose(input: {
  slotInstantMs: number;
  nowMs: number;
  administrationStatus?: string | null;
  dueWindowMinutes?: number;
}): DoseStatusBucket {
  const status = input.administrationStatus ?? null;
  if (status && DOSE_GIVEN_STATUSES.has(status)) return 'given';
  if (status && DOSE_NOT_GIVEN_STATUSES.has(status)) return 'not_given';

  const windowMs = Math.max(0, input.dueWindowMinutes ?? 60) * 60_000;
  const diffMs = input.slotInstantMs - input.nowMs;
  if (diffMs > windowMs) return 'upcoming';
  if (diffMs >= -windowMs) return 'due';
  return 'overdue';
}
